import {renderToStaticMarkup} from "react-dom/server";
import {getCitationFilePath} from "../../api";

type HtmlParsedAnswer = {
    answerHtml: string;
    citations: string[];
};

export function parseAnswerToHtml(answer: string, isStreaming: boolean): HtmlParsedAnswer {
    const citations: string[] = [];

    // Trim whitespace and handle ongoing citation during streaming
    let trimmedAnswer = answer.trim();
    if (isStreaming) {
        trimmedAnswer = removeUnfinishedCitation(trimmedAnswer);
    }

    // Split text by citations enclosed in [ ]
    const parts = trimmedAnswer.split(/\[([^\]]+)\]/g);

    // Convert text and citations into HTML with Markdown support
    const answerHtml = parts
        .map((part, index) =>
            index % 2 === 0 ? convertMarkdownToHTML(part, isStreaming) : formatCitation(part, citations)
        )
        .join("");

    return { answerHtml, citations };
}

// Removes an incomplete citation at the end of a streaming answer
function removeUnfinishedCitation(text: string): string {
    const lastOpenBracket = text.lastIndexOf("[");
    const lastCloseBracket = text.lastIndexOf("]");

    return lastOpenBracket > lastCloseBracket ? text.substring(0, lastOpenBracket) : text;
}

// Converts citation text into a numbered <sup> link
function formatCitation(citationText: string, citations: string[]): string {
    let citationIndex = citations.indexOf(citationText);
    if (citationIndex === -1) {
        citationIndex = citations.push(citationText) - 1; // Store and get 1-based index
    }

    return renderToStaticMarkup(
        <a className="supContainer" title={citationText}>
            <sup>{citationIndex + 1}</sup>
        </a>
    );
}

// Converts simple Markdown syntax to HTML
function convertMarkdownToHTML(markdown: string, isStreaming: boolean = false): string {
    // First, apply other markdown conversions
    let result = markdown
        .replace(/#+ (.*?)(\n|$)/g, "<h4>$1</h4>") // Convert # Header
        .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")   // Convert **bold**
        .replace(/\*(.*?)\*/g, "<i>$1</i>")       // Convert *italic*
        .replace(/`([^`]+)`/g, "<code>$1</code>"); // Convert `code`
    
    // Check if we're inside a table (for streaming support)
    const hasOpenTable = /<table[^>]*>/i.test(result);
    const hasCloseTable = /<\/table>/i.test(result);
    const lastTableIndex = result.lastIndexOf('<table');
    const lastCloseTableIndex = result.lastIndexOf('</table>');
    const isInsideTable = hasOpenTable && (!hasCloseTable || lastTableIndex > lastCloseTableIndex);
    
    // Split by table tags to preserve table content
    const tableRegex = /(<table[^>]*>[\s\S]*?<\/table>)/gi;
    const parts: string[] = [];
    let lastIndex = 0;
    let match;
    let foundTables = false;
    
    while ((match = tableRegex.exec(result)) !== null) {
        foundTables = true;
        // Add text before table (convert newlines to <br>)
        if (match.index > lastIndex) {
            parts.push(result.substring(lastIndex, match.index).replace(/\n/g, "<br>"));
        }
        // Wrap table in a scrollable container
        parts.push(`<div class="tableWrapper">${match[1]}</div>`);
        lastIndex = match.index + match[0].length;
    }
    
    // Handle incomplete table during streaming
    if (isStreaming && isInsideTable && !foundTables) {
        // Find the start of the incomplete table
        const tableStartMatch = result.match(/<table[^>]*>/i);
        if (tableStartMatch && tableStartMatch.index !== undefined) {
            const tableStartIndex = tableStartMatch.index;
            // Add text before table (convert newlines to <br>)
            if (tableStartIndex > lastIndex) {
                parts.push(result.substring(lastIndex, tableStartIndex).replace(/\n/g, "<br>"));
            }
            // Wrap incomplete table in a scrollable container
            // Newlines inside are preserved as \n (not converted to <br>)
            const incompleteTable = result.substring(tableStartIndex);
            parts.push(`<div class="tableWrapper">${incompleteTable}</div>`);
            return parts.join("");
        }
    }
    
    // Handle case where we have a complete table but there's text after it
    // OR we have an incomplete table at the end (during streaming)
    if (lastIndex < result.length) {
        const remainingText = result.substring(lastIndex);
        // If we're inside a table (incomplete), wrap it
        if (isStreaming && isInsideTable && lastTableIndex >= lastIndex) {
            // We have an incomplete table - wrap everything from the table start
            const beforeTable = result.substring(lastIndex, lastTableIndex);
            const incompleteTable = result.substring(lastTableIndex);
            if (beforeTable) {
                parts.push(beforeTable.replace(/\n/g, "<br>"));
            }
            parts.push(`<div class="tableWrapper">${incompleteTable}</div>`);
        } else {
            // Regular text after table - convert newlines to <br>
            parts.push(remainingText.replace(/\n/g, "<br>"));
        }
    }
    
    // If no tables found, just convert all newlines
    if (parts.length === 0) {
        return result.replace(/\n/g, "<br>");
    }
    
    return parts.join("");
}